<template>
  <div v-if="tab" class="w-screen h-screen pb-safe overflow-hidden flex flex-col">
    <top-bar :show-back="true" @back="exitOrderManagement()">
      <template #center>
        <div v-if="tab" class="flex items-center gap-2 font-heading font-bold text-xl">
          <span v-if="tab.code">
            <span class="font-bold">{{ tab.code }}</span>
            <span v-if="tab.tableName"> - {{ tab.tableName }}</span>
            <span v-else-if="tab.name"> - {{ tab.name }}</span>
          </span>
          <span v-else-if="tab.tableName">{{ tab.tableName }}</span>
          <span v-else>{{ tab.name }}</span>

          <l-dropdown
            v-model:open="openTabOptions"
            placement="bottom"
            :mark-selected="false"
            :options="tabOptions"
          >
            <l-icon
              v-if="tab.open"
              name="bracket-down"
              class="text-v-300"
              @click="openTabOptions = true"
            />
          </l-dropdown>
        </div>
      </template>
    </top-bar>
    <div
      class="flex w-full h-full overflow-hidden relative"
      :class="{
        'mb-[5.5rem]': !showOpenTab
      }"
    >
      <div class="flex-1 w-0 flex flex-col mt-3">
        <div class="flex justify-between px-3 mb-2 gap-2">
          <ordering-mode-selector :tab-id="tabId" />
          <seat-or-course-selector
            v-model:selected-seat="selectedSeat"
            v-model:selected-course="selectedCourse"
            class="flex-1"
            :tab-id="tabId"
          />
        </div>
        <menu-browser
          :tab-id="tabId"
          :customer-points="availablePoints"
          :customer-used-promotions="customerUsedPromotions"
          @selected-product="configureProduct"
        />
      </div>
      <open-tab
        v-if="showOpenTab"
        v-model:selected-seat="selectedSeat"
        v-model:selected-course="selectedCourse"
        class="absolute t-0 left-0 w-full z-20"
        :tab-id="tabId"
        :catalog-id="catalogId"
        :selected-product-id="selectedProductId"
        @selected-product="editProduct"
        @selected-combo-product="editComboProduct"
      />
    </div>

    <resend-kitchen-orders-component
      v-if="resendKitchenOrders"
      :tab-id="tabId"
      @close="resendKitchenOrders = false"
    />
    <div
      class="absolute bottom-safe left-0 w-full p-4 pb-6 bg-n-800"
      :class="{ hidden: showOpenTab, absolute: !showOpenTab }"
    >
      <l-button class="w-full" @click="showOpenTab = true">
        {{ t('ordering.see-order') }}
        <l-badge :value="productCount" mode="secondary" class="ml-2" />
      </l-button>
    </div>
    <link-customer-component
      v-if="linkCustomerModal"
      :tab-id="tabId"
      @close="linkCustomerModal = false"
    ></link-customer-component>
    <assign-table v-model:is-active="showAssignTablePopup" :tab-ids="[tabId]" />
    <div v-if="editingProduct || editingCombo" class="fixed inset-0 h-full w-full bg-n-0 z-40">
      <product-editor
        v-if="editingProduct"
        show-quantity
        emit-close
        :product="editingProduct"
        :catalog-id="catalogId"
        :tab-id="tabId"
        :selected-seat="selectedSeat"
        @close="editingProduct = null"
      />
      <combo-editor
        v-if="editingCombo"
        :editing-combo="editingCombo.edit"
        :catalog-combo="editingCombo.catalog"
        :combo-product="editingCombo.comboProduct"
        :tab-id="tabId"
        :seat="selectedSeat"
        @close="editingCombo = null"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { storeToRefs } from 'pinia'
import { computed, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'

import { ProductPriceCalculator } from '@last/core'
import type { LIconName } from '@last/core-ui/paprika'
import { LBadge, LButton, LDropdown, LIcon, useDialog } from '@last/core-ui/paprika'
import type { Option } from '@last/core-ui/paprika/components/dropdown/types'

import { getCustomerPromotions } from '@/api/customers'
import TopBar from '@/components/TopBarMobile.vue'
import { useKitchenOrders } from '@/composables/useKitchenOrders'
import { useTabs } from '@/composables/useTabs'
import LinkCustomerComponent from '@/modules/ordering/LinkCustomer.vue'
import MenuBrowser from '@/modules/ordering/MenuBrowser.vue'
import OpenTab from '@/modules/ordering/OpenTab.vue'
import OrderingModeSelector from '@/modules/ordering/OrderingModeSelector.vue'
import ResendKitchenOrdersComponent from '@/modules/ordering/ResendKitchenOrders.vue'
import SeatOrCourseSelector from '@/modules/ordering/SeatOrCourseSelector.vue'
import AssignTable from '@/modules/tabs/AssignTable.vue'
import { useCatalogStore } from '@/store/catalog'
import { useConfigStore } from '@/store/config'
import { usePromotionsStore } from '@/store/promotions'
import { useReservationsStore } from '@/store/reservations'
import { useTabsStore } from '@/store/tabs'
import type { CatalogProduct, OpenProduct } from '@/types'

import ComboEditor from './ComboEditor.vue'
import ProductEditor from './ProductEditor.vue'

const route = useRoute()
const router = useRouter()
const dialog = useDialog()
const { t } = useI18n()

const tabId = computed(() => route.params.tabId as string)

const tabsStore = useTabsStore()
const catalogStore = useCatalogStore()
const promotionsStore = usePromotionsStore()
const configStore = useConfigStore()
const reservationStore = useReservationsStore()

const { addProduct, updateProductModifiers, updateCourse } = tabsStore
const { updateCustomerPromotions } = promotionsStore
const { usedPointsInTab } = storeToRefs(promotionsStore)
const { config } = storeToRefs(configStore)
const { sendKitchenOrder, pendingOrders } = useKitchenOrders(tabId)
const { tab, closeTab, catalogId, allProducts } = useTabs(tabId)
const { sortedReservations } = storeToRefs(reservationStore)

const showAssignTablePopup = ref(false)
const selectedSeat = ref<number | null>(null)
const selectedCourse = ref<string>('')
const editingProduct = ref<any>(null)
const editingCombo = ref<any>(null)
const editingSeat = ref()
const resendKitchenOrders = ref(false)
const customerPoints = ref(0)
const customerUsedPromotions = ref<any[]>([])
const linkCustomerModal = ref(false)
const showOpenTab = ref(false)
const openTabOptions = ref(false)

const tabOptions = computed<Option[]>(() => {
  const options = [
    {
      icon: 'user' as LIconName,
      label: tab.value.customerInfo?.name ?? t('ordering.link-customer'),
      value: 'link-customer',
      onClick: () => {
        linkCustomerModal.value = true
        openTabOptions.value = false
      }
    },
    {
      icon: (tab.value.tables.length > 0 ? 'double-arrow-sides' : 'plus') as LIconName,
      label: tab.value.tables.length > 0 ? t('floorplan.move-account') : t('tabs.assign-to-table'),
      value: 'assign-to-table',
      onClick: () => {
        showAssignTablePopup.value = true
        openTabOptions.value = false
      }
    },

    {
      icon: 'close' as LIconName,
      label: t('close-tab.close-tab'),
      value: 'close',
      onClick: () => {
        closeTab()
        openTabOptions.value = false
      }
    }
  ]

  const hasReservation = sortedReservations.value.some(
    reservation => reservation.tabId === tabId.value
  )
  if (!hasReservation) {
    options.splice(2, 0, {
      icon: 'calendar' as LIconName,
      label: t('ordering.link-reservation'),
      value: 'link-reservation',
      onClick: () => {
        router.push({ name: 'linkReservations', query: { tabId: tabId.value } })
        openTabOptions.value = false
      }
    })
  }

  return options
})

const selectedProductId = computed(() => (editingProduct.value ? editingProduct.value.id : null))

const productCount = computed(() => {
  return allProducts.value.reduce((total, product) => {
    total += product.quantity
    return total
  }, 0)
})

const tabCustomerId = computed(() => tab.value?.customerId)

const availablePoints = computed(() => {
  return customerPoints.value - usedPointsInTab.value(tabId.value)
})

watch(
  () => productCount.value,
  (oldCount, newCount) => {
    if (oldCount !== newCount) {
      editingProduct.value = null
    }
  }
)

watch(tabCustomerId, (oldId, newId) => {
  if (oldId !== newId) {
    updatePromotionsInfo()
  }
})

watch(selectedSeat, value => {
  if (value !== route.query.selectedSeat) {
    router.replace({
      query: { ...route.query, selectedSeat: value }
    })
  }
})

onMounted(async () => {
  updatePromotionsInfo()
})

async function updatePromotionsInfo() {
  let points = 0
  const customerId = tab.value?.customerId
  if (customerId) {
    const response = await getCustomerPromotions(customerId)
    points = response.points
    updateCustomerPromotions({
      customerId,
      customerPromotions: response.customerPromotions
    })
    customerUsedPromotions.value = response.customerUsedPromotions
  }
  customerPoints.value = points
}

function exitOrderManagement() {
  if (showOpenTab.value) {
    showOpenTab.value = false
    return
  }
  if (
    pendingOrders.value &&
    tab.value.activationTime &&
    config.value.enableKitchenOrders &&
    tab.value.open
  ) {
    dialog({
      title: t('unsent-dialog.title'),
      content: t('unsent-dialog.message'),
      secondaryLabel: t('unsent-dialog.cancel'),
      onConfirm: async () => {
        await sendKitchenOrder('all')
        router.replace({ name: 'pos' })
      },
      onSecondary: () => {
        router.replace({ name: 'pos' })
      }
    })
  } else {
    router.replace({ name: 'pos' })
  }
}

async function saveProduct(product: any) {
  const productPricing = ProductPriceCalculator.calculateProductPricing(product)
  if (product.id) {
    updateProductModifiers({
      productId: product.id,
      modifiers: product.modifiers,
      comments: product.comments,
      productPricing
    })
    updateCourse({
      productId: product.id,
      course: product.course
    })
  } else {
    addProduct({
      tabId: tabId.value,
      seat: selectedSeat.value,
      product: {
        ...product,
        ...productPricing
      }
    })
  }
  editingProduct.value = null
}

function configureProduct(product: CatalogProduct | OpenProduct, quantity = 1) {
  const parentProduct = catalogStore.getProductById(product.id)
  if ('categories' in product && product.categories) {
    const newCombo = {
      catalog: { ...product, ...parentProduct },
      edit: null
    }
    editingCombo.value = newCombo
  } else {
    const newProduct = {
      ...product,
      ...parentProduct,
      price: product.price,
      course: selectedCourse.value || product.course,
      id: null,
      parentProduct: product.id,
      quantity
    }

    const hasMandatoryModifiers =
      !!newProduct.modifierGroups && newProduct.modifierGroups.some(group => group.min > 0)
    if (hasMandatoryModifiers) {
      editingProduct.value = newProduct
    } else {
      saveProduct({ ...newProduct, modifiers: [] })
    }
  }
}

const editComboProduct = ({ product, comboProduct }: { product: any; comboProduct: any }) => {
  const parentProduct = catalogStore.getProductById(product.parentProduct)
  if (parentProduct && parentProduct.categories) {
    editingCombo.value = {
      catalog: parentProduct,
      edit: product,
      comboProduct
    }
  }
}

const editProduct = (product: any) => {
  if (!product) {
    editingProduct.value = null
    return
  }
  editingSeat.value = selectedSeat.value
  const parentProduct = catalogStore.getProductById(product.parentProduct)
  if (parentProduct && parentProduct.categories) {
    editingCombo.value = {
      catalog: parentProduct,
      edit: product
    }
  } else {
    editingProduct.value = {
      ...parentProduct,
      quantity: product.quantity,
      modifiers: product.modifiers,
      comments: product.comments,
      id: product.id,
      parentProduct: (parentProduct && parentProduct.id) || null,
      course: product.course,
      price: product.price,
      name: product.name,
      discountType: product.discountType,
      discountAmount: product.discountAmount,
      pointsExpense: product.pointsExpense
    }
  }
}
</script>
